import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { supabase } from '../../lib/supabase';
import {
  AUTH_UNREACHABLE_MESSAGE,
  isAuthConnectionError,
} from '../../shared/auth/auth-connection-error';
import { syntheticEmail } from '../../shared/auth/student-login';
import { Button } from '../../shared/ui/Button';
import { FormError } from '../../shared/ui/FormError';
import { Input } from '../../shared/ui/Input';
import { Label } from '../../shared/ui/Label';
import { StatusBanner } from '../../shared/ui/StatusBanner';

import { AuthLayout } from './AuthLayout';
import { getInstituteBrand } from './institute-login-api';
import s from './InstituteLoginPage.module.css';
import { instituteLoginSchema, type InstituteLoginInput } from './schemas';

export function InstituteLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { slug = '' } = useParams<{ slug: string }>();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const brand = useQuery({
    queryKey: ['institute', 'brand', slug],
    queryFn: () => getInstituteBrand(slug),
    enabled: slug !== '',
    staleTime: 5 * 60_000,
  });
  const instituteName = brand.data?.name ?? null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InstituteLoginInput>({
    resolver: zodResolver(instituteLoginSchema),
    defaultValues: { username: '', password: '' },
  });

  async function onSubmit(values: InstituteLoginInput): Promise<void> {
    setSubmitError(null);
    const email = syntheticEmail(slug, values.username);
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password: values.password,
    });
    if (error !== null) {
      // The generic message exists so a failed attempt never reveals whether
      // the username exists — but an unreachable server reveals nothing about
      // the account either way, and telling a student their password is wrong
      // during an outage sends them hunting for a problem they do not have.
      setSubmitError(
        isAuthConnectionError(error) ? AUTH_UNREACHABLE_MESSAGE : 'Incorrect username or password.',
      );
      return;
    }
    navigate('/app', { replace: true });
  }

  return (
    <AuthLayout
      pitchTitle={
        <>
          Your mocks, <em>ready</em> when you are.
        </>
      }
      pitchBody="Sign in with the username and password your institute gave you to start assigned mocks and review your results."
    >
      <span className={s.eyebrow}>Student sign in</span>
      <h1 className={s.title}>{instituteName ?? 'Log in'}</h1>
      <p className={s.subtitle}>Use the username and password your institute shared with you.</p>

      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(onSubmit)(event);
        }}
        className={s.form}
      >
        <div>
          <Label htmlFor="username">Username</Label>
          <Input
            id="username"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="next"
            placeholder="your username"
            invalid={errors.username !== undefined}
            aria-describedby={errors.username ? 'username-error' : undefined}
            {...register('username')}
          />
          <FormError id="username-error" message={errors.username?.message} />
        </div>

        <div>
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            autoComplete="current-password"
            enterKeyHint="go"
            placeholder="••••••••"
            invalid={errors.password !== undefined}
            aria-describedby={errors.password ? 'password-error' : undefined}
            {...register('password')}
          />
          <FormError id="password-error" message={errors.password?.message} />
        </div>

        {submitError !== null ? <StatusBanner tone="error">{submitError}</StatusBanner> : null}

        <Button type="submit" size="lg" fullWidth loading={isSubmitting}>
          Log in
        </Button>

        <p className={s.help}>Forgot your password? Ask your institute to reset it for you.</p>
      </form>
    </AuthLayout>
  );
}
